/**
 * <mellow-toast-region>: renders toasts from its <template>.
 *
 * Show toasts with Mellow.toast({ text, heading, variant, duration }), the `mellow-toast`
 * window event (Livewire: Mellow::toast()), or flashed session toasts.
 */
export class MellowToastRegion extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        regions.add(this);

        // Stay in the top layer (and in the accessibility tree) so the live region is ready.
        this.showPopover?.();

        // Toasts flashed to the session before a redirect.
        const flashed = this.querySelector('script[type="application/json"]');
        if (flashed) {
            JSON.parse(flashed.textContent || '[]').forEach((toast) => this.show(toast));
            flashed.remove();
        }
    }

    disconnectedCallback() {
        regions.delete(this);
    }

    show({ text = '', heading = null, variant = null, duration = 5000 } = {}) {
        const template = this.querySelector('template');
        const list = this.querySelector('.mellow-toast-list');
        if (!template || !list) return;

        const toast = template.content.firstElementChild.cloneNode(true);
        toast.querySelector('[data-heading]').textContent = heading ?? '';
        toast.querySelector('[data-heading]').hidden = !heading;
        toast.querySelector('[data-text]').textContent = text;
        toast.querySelector('[data-text]').hidden = !text;
        if (variant) toast.dataset.variant = variant;

        // The list is a polite live region; errors additionally interrupt as alerts.
        // (on the body: list items can't take a role without breaking the list).
        if (variant === 'danger') toast.querySelector('.mellow-toast-body').setAttribute('role', 'alert');

        const close = () => this.dismiss(toast);
        toast.querySelector('[data-close]').addEventListener('click', close);

        // Pause auto-dismiss while the user is hovering or focusing the toast.
        let timer;
        let remaining = duration;
        let started;
        const start = () => {
            if (!duration) return;
            started = Date.now();
            timer = setTimeout(close, remaining);
        };
        const pause = () => {
            clearTimeout(timer);
            remaining -= Date.now() - started;
        };
        toast.addEventListener('pointerenter', pause);
        toast.addEventListener('pointerleave', start);
        toast.addEventListener('focusin', pause);
        toast.addEventListener('focusout', start);

        list.append(toast);
        this.bringToFront();
        start();

        return close;
    }

    dismiss(toast) {
        if (toast.hasAttribute('data-leaving')) return;
        toast.setAttribute('data-leaving', '');
        const remove = () => toast.remove();
        toast.addEventListener('animationend', remove, { once: true });
        setTimeout(remove, 400);
    }

    /** Re-enter the top layer so toasts appear above modals opened after the region. */
    bringToFront() {
        if (!this.showPopover || !document.querySelector('dialog[open]')) return;
        if (this.matches(':popover-open')) this.hidePopover();
        this.showPopover();
    }
}

const regions = new Set();

export function toast(options) {
    if (typeof options === 'string') options = { text: options };
    const region = [...regions].at(-1);
    if (!region) {
        console.warn('Mellow: add <mellow:toast /> to your layout to show toasts.');
        return;
    }
    return region.show(options);
}

export function registerToastBehaviors() {
    window.addEventListener('mellow-toast', (event) => toast(event.detail ?? {}));
}
